// MessageListContext.tsx
import React, {
  createContext,
  useContext,
  useReducer,
  useMemo,
  Dispatch,
} from 'react';
import { Message } from 'react-native-gifted-chat';

// Define your state shape
interface MessageListState {
  data: any[];
  messageArray: typeof Message[];
  activeFilter: 'whatsapp' | 'internal' | null;
  conversationContext: 'whatsapp' | 'internal';
  chatHistory: any[]; // Persist chat history across LazyMessages remounts
  isLoadingChatHistory: boolean; // Persist loading state
  hoveredChatId: string | null; // Persist hover state
}

const initialState: MessageListState = {
  data: [],
  messageArray: [],
  activeFilter: null,
  conversationContext: 'internal',
  chatHistory: [], // Initialize empty
  isLoadingChatHistory: false,
  hoveredChatId: null,
};

// Define action types
type Action =
  | { type: 'SET_DATA'; payload: any[] }
  | { type: 'UPDATE_BADGE'; userId: string; amount: number, event: any }
  | { type: 'SET_MESSAGE_ARRAY'; payload: typeof Message[] }
  | { type: "UPDATE_STATUS"; userId: string; status: string }
  | { type: "UPDATE_CONVERSATION_ID"; payload: {
    conversationId: string;
    userId: string;
  } }
  | { type: 'SET_ACTIVE_FILTER'; payload: 'whatsapp' | 'internal' | null }
  | { type: 'SET_CONVERSATION_CONTEXT'; payload: 'whatsapp' | 'internal' }
  | { type: 'SEND_WHATSAPP_MESSAGE'; payload: { to: string; message: string; businessPhoneNumberId?: string } }
  | { type: 'SEND_INTERNAL_MESSAGE'; payload: { to: string; message: string } }
  | { type: 'UPDATE_WHATSAPP_STATUS'; payload: { conversationId: string; status: 'delivered' | 'read' | 'sent' | 'failed' } }
  | { type: 'MARK_WHATSAPP_READ'; payload: { conversationId: string; messageId: string } }
  | { type: 'SET_CHAT_HISTORY'; payload: any[] }
  | { type: 'SET_LOADING_CHAT_HISTORY'; payload: boolean }
  | { type: 'SET_HOVERED_CHAT_ID'; payload: string | null };

function messageListReducer(state: MessageListState, action: Action): MessageListState {
  switch (action.type) {
    case 'SET_DATA':
      return { ...state, data: action.payload };
    case 'SET_ACTIVE_FILTER':
      return { ...state, activeFilter: action.payload };
    case 'SET_CONVERSATION_CONTEXT':
      return { ...state, conversationContext: action.payload };
    case 'UPDATE_STATUS': {
      const index = state.data.findIndex(item => item._id === action.userId);
      if (index === -1) return state;
      // Only update the affected item
      const updatedItem = { ...state.data[index], status: action.status, lastModified: new Date() };
      // If status hasn't changed, return state
      if (updatedItem.status === state.data[index].status) return state;
      const newData = [...state.data];
      newData[index] = updatedItem;
      return { ...state, data: newData };
    }
    case 'UPDATE_WHATSAPP_STATUS': {
      const index = state.data.findIndex(item => 
        item.conversationId === action.payload.conversationId || 
        item.id === action.payload.conversationId
      );
      if (index === -1) return state;
      const updatedItem = { 
        ...state.data[index], 
        status: action.payload.status, 
        lastModified: new Date(),
        messageStatus: action.payload.status
      };
      const newData = [...state.data];
      newData[index] = updatedItem;
      return { ...state, data: newData };
    }
    case 'MARK_WHATSAPP_READ': {
      const index = state.data.findIndex(item => 
        item.conversationId === action.payload.conversationId ||
        item.id === action.payload.conversationId
      );
      if (index === -1) return state;
      const updatedItem = { 
        ...state.data[index], 
        unreadCount: 0,
        badge: 0,
        lastReadMessageId: action.payload.messageId,
        lastModified: new Date() 
      };
      const newData = [...state.data];
      newData[index] = updatedItem;
      return { ...state, data: newData };
    }
    case 'UPDATE_STATUS': {
      const index = state.data.findIndex(item => item._id === action.userId);
      if (index === -1) return state;
      // Only update the affected item
      const updatedItem = { ...state.data[index], status: action.status, lastModified: new Date() };
      // If status hasn't changed, return state
      if (updatedItem.status === state.data[index].status) return state;
      const newData = [...state.data];
      newData[index] = updatedItem;
      return { ...state, data: newData };
    }
    case 'UPDATE_CONVERSATION_ID': {
      const index = state.data.findIndex(item => item._id === action.payload.userId);
      if (index === -1) return state;
      // Only update the affected item
      const updatedItem = { ...state.data[index], conversationId: action.payload.conversationId, lastModified: new Date() };
      // If conversationId hasn't changed, return state
      if (updatedItem.conversationId === state.data[index].conversationId) return state;
      const newData = [...state.data];
      newData[index] = updatedItem;
      return { ...state, data: newData };
    }
    case 'UPDATE_BADGE': {
      const index = state.data.findIndex(item => {
        const itemConversationId = item.conversationId || item.id || item._id;
        const participantId = item.participantId || item.user?._id || item.id;
        return (
          item._id === action.userId ||
          participantId === action.userId ||
          (action.event?.convoId && itemConversationId === action.event.convoId)
        );
      });
      if (index === -1) return state;
      console.log(action.event, 'event');
      console.log('updating badge of', state.data[index]);
      const currentItem = state.data[index];
      const updatedItem = {
        ...currentItem,
        conversationId: action.event?.convoId || currentItem.conversationId,
        lastEvent: action.event,
      };
      if (typeof action.amount === 'number') {
        const nextBadge = (currentItem.badge || 0) + action.amount;
        updatedItem.badge = nextBadge < 0 ? 0 : nextBadge;
      }
      updatedItem.lastModified = new Date();
      if (
        updatedItem.badge === currentItem.badge &&
        updatedItem.conversationId === currentItem.conversationId
      ) {
        return state;
      }
      const newData = [...state.data];
      newData[index] = updatedItem;
      return { ...state, data: newData };
    }
    case 'SET_MESSAGE_ARRAY':
      console.log('📱 MessageListContext - SET_MESSAGE_ARRAY action:', {
        payloadLength: action.payload?.length || 0,
        payloadType: typeof action.payload,
        isArray: Array.isArray(action.payload),
        firstMessage: action.payload?.[0]
      });
      const newState = { ...state, messageArray: action.payload };
      console.log('📱 MessageListContext - State updated:', {
        newMessageArrayLength: newState.messageArray.length,
        timestamp: new Date().toISOString()
      });
      return newState;
    case 'SET_CHAT_HISTORY':
      console.log('💬 MessageListContext - SET_CHAT_HISTORY action:', {
        payloadLength: action.payload?.length || 0,
        timestamp: new Date().toISOString()
      });
      return { ...state, chatHistory: action.payload };
    case 'SET_LOADING_CHAT_HISTORY':
      return { ...state, isLoadingChatHistory: action.payload };
    case 'SET_HOVERED_CHAT_ID':
      return { ...state, hoveredChatId: action.payload };
    default:
      return state;
  }
}

// Create context type that includes state and dispatch
interface MessageListContextProps {
  state: MessageListState;
  dispatch: Dispatch<Action>;
}

const MessageListContext = createContext<MessageListContextProps | undefined>(undefined);

export const MessageListProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(messageListReducer, initialState);
  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);
  return (
    <MessageListContext.Provider value={value}>
      {children}
    </MessageListContext.Provider>
  );
};

export const useMessageList = () => {
  const context = useContext(MessageListContext);
  if (!context) {
    throw new Error('useMessageList must be used within a MessageListProvider');
  }
  return context;
};
